import { useEffect, useState } from 'react';
import UserListComponent from './UserListComponent';
import ProfileComponent from '../Profile/ProfileComponent';
import Typography from '@mui/material/Typography';
import {
  getUsers,
  getUnits,
  getCampaigns,
  getAudiences,
  getDepartments,
  getNoOfTenantSeatsRemaining,
  getTypes,
  resendPassword,
  disableUser,
  enableUser,
  adminReinitUserSMSMFA,
  getCustomFields,
} from './adminPageRequest';
import Button from '@mui/material/Button';
import ConfirmationModal from './ConfirmationModal';
import AddUserModal from './AddUserModal';
import { useDispatch, useSelector } from 'react-redux';
import { openSnackbar } from '../AppSnackbar/snackbarSlice';
import EnumListContainer from './EnumListContainer';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Stack, Box, Tooltip, Zoom } from '@mui/material';
import AdminCategorySelect from './AdminCategorySelect';
import AddIcon from '@mui/icons-material/Add';
import { PrivilegeLevels } from '../../shared/constants/PrivilegeLevels';
import { updateUserWithPrivilege } from '../../shared/functions/userFunctions';
import { userPrivilegeMap } from '../../shared/constants/constants';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CustomFieldsContainer from './CustomFieldsContainer';
//import {sendResetPasswordLink} from './emailServiceAdapter'

const spinnerSx = {
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
};
function AdminPage({ editingUserPrivilege, dropdownTable }) {
  const dispatch = useDispatch();
  const contentLogActivated = useSelector((state) => state.user.addonControls.contentLogActivated === true);
  const engagementLogActivated = useSelector((state) => state.user.addonControls.engagementLogActivated === true);

  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [seatsRemaining, setSeatsRemaining] = useState(0);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [confirmationReset, setConfirmationReset] = useState(false);
  const [confirmationResetMFA, setConfirmationResetMFA] = useState(false);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [enumModalOpen, setEnumModalOpen] = useState(false);
  const [category, setCategory] = useState('Users');
  const [isEditing, setIsEditing] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const [selectedEnum, setSelectedEnum] = useState(null);

  const queryClient = useQueryClient();
  const isUsersCategory = category === 'Users';
  const isUnitsCategory = category === 'Units';
  const isTypesCategory = category === 'Types';
  const isDepartmentsCategory = category === 'Departments';
  const isCampaignsCategory = category === 'Campaigns';
  const isCustomFieldsCategory = category === 'Custom Fields';

  const updateUserMutation = useMutation({
    mutationFn: async (updatedInfo) => {
      const response = await updateUserWithPrivilege(updatedInfo);
      await queryClient.invalidateQueries({ queryKey: ['tenantSeats'] });
      await queryClient.invalidateQueries({ queryKey: ['dropdown-table'] });
      return response;
    },
  });

  const disableUserMutation = useMutation({
    mutationFn: (userId) => disableUser(userId),
  });

  const enableUserMutation = useMutation({
    mutationFn: (userId) => enableUser(userId),
  });

  const resetUserMfaMutation = useMutation({
    mutationFn: (userId) => adminReinitUserSMSMFA(userId),
  });

  const resendPasswordMutation = useMutation({
    mutationFn: (userId) => resendPassword(userId),
  });

  const usersQuery = useQuery({
    queryKey: ['users', 'adminPage'],
    queryFn: () => getUsers(),
    enabled: isUsersCategory,
  });

  const typesQuery = useQuery({
    queryKey: ['types', 'adminPage'],
    queryFn: () => getTypes(),
    enabled: isTypesCategory,
  });

  const unitsQuery = useQuery({
    queryKey: ['units', 'adminPage'],
    queryFn: () => getUnits(),
    enabled: isUnitsCategory,
  });

  const deptsQuery = useQuery({
    queryKey: ['depts', 'adminPage'],
    queryFn: () => getDepartments(),
    enabled: isDepartmentsCategory,
  });

  const campaignsQuery = useQuery({
    queryKey: ['campaigns', 'adminPage'],
    queryFn: () => getCampaigns(),
    enabled: isCampaignsCategory,
  });

  const tenantSeatsQuery = useQuery({
    queryKey: ['tenantSeats', 'adminPage'],
    queryFn: () => getNoOfTenantSeatsRemaining(),
    enabled: isUsersCategory,
  });

  const expertCustomFieldsQuery = useQuery({
    queryKey: ['expertCustomFields', 'adminPage'],
    queryFn: () => getCustomFields('Expert'),
    enabled: isCustomFieldsCategory,
  });

  const journalistCustomFieldsQuery = useQuery({
    queryKey: ['journalistCustomFields', 'adminPage'],
    queryFn: () => getCustomFields('Journalist'),
    enabled: isCustomFieldsCategory,
  });

  const outletCustomFieldsQuery = useQuery({
    queryKey: ['outletCustomFields', 'adminPage'],
    queryFn: () => getCustomFields('Org'),
    enabled: isCustomFieldsCategory,
  });

  const communityMemberCustomFieldsQuery = useQuery({
    queryKey: ['communityMemberCustomFields', 'adminPage'],
    queryFn: () => getCustomFields('CommunityMember'),
    enabled: isCustomFieldsCategory && engagementLogActivated,
  });

  const organizationCustomFieldsQuery = useQuery({
    queryKey: ['organizationCustomFields', 'adminPage'],
    queryFn: () => getCustomFields('Organization'),
    enabled: isCustomFieldsCategory && engagementLogActivated,
  });

  const audiencesQuery = useQuery({
    queryKey: ['audiences', 'adminPage'],
    queryFn: () => getAudiences(),
    enabled: category === 'Audiences' && contentLogActivated,
  });

  const handleCategoryChange = (event) => {
    setCategory(event.target.value);
  };

  const categoryQueryStates = {
    Users: [usersQuery, tenantSeatsQuery],
    Units: [unitsQuery],
    Types: [typesQuery],
    Departments: [deptsQuery],
    Campaigns: [campaignsQuery],
    Audiences: [audiencesQuery],
    'Custom Fields': [
      expertCustomFieldsQuery,
      journalistCustomFieldsQuery,
      outletCustomFieldsQuery,
      communityMemberCustomFieldsQuery,
      organizationCustomFieldsQuery,
    ],
  };

  const updateUserList = (user) => {
    const index = users.findIndex((userIt) => {
      return userIt._id === user._id;
    });

    if (index > -1) {
      const tempUsers = users;
      tempUsers[index] = user;
      setUsers(tempUsers);
    }
  };

  const addUserToList = (user) => {
    setUsers([...users, user]);
    setSelectedUser(user);
    if (seatsRemaining !== false && user.privilege !== userPrivilegeMap.broadsightAdmin) {
      setSeatsRemaining(seatsRemaining - 1);
    }
  };

  const getAvatarLetter = (user) => {
    let nameTokens = user.name.split(' ') || null;
    return nameTokens
      ? `${nameTokens[0].charAt(0).toUpperCase()}${nameTokens[1].charAt(0).toUpperCase()}`
      : `${user.username.charAt(0).toUpperCase()}`;
  };

  const getListItemSubTitle = (user) => {
    return user.name || '';
  };
  const getListItemTitle = (user) => {
    return user.username;
  };

  const replaceStaleUser = (user) => {
    const indexOfUser = users.findIndex((oldUser) => {
      return user._id === oldUser._id;
    });

    if (indexOfUser < 0) {
      throw new Error(`Could not find user ${user.id}`);
    }

    const newUsersArray = users;
    newUsersArray[indexOfUser] = user;

    setUsers(newUsersArray);
  };

  const hasAdminPrivilege = () => {
    return editingUserPrivilege >= PrivilegeLevels.admin.level;
  };

  const onReset = async () => {
    try {
      await resendPasswordMutation.mutateAsync(selectedUser._id);
      dispatch(openSnackbar({ severity: 'warning', message: `sent password reset successfully` }));
    } catch (err) {
      console.error(err);
      dispatch(openSnackbar({ severity: 'error', message: `failed to send password reset` }));
    }
  };

  const onDisable = async () => {
    try {
      const user = await disableUserMutation.mutateAsync(selectedUser._id);
      setConfirmationOpen(false);
      setConfirmationReset(false);
      replaceStaleUser(user);
      setSelectedUser(user);

      // AWS cognito calls are slow + expensive, so we just update the # of seats
      // the user sees on the client side.
      if (seatsRemaining !== false && user.privilege !== userPrivilegeMap.broadsightAdmin) {
        setSeatsRemaining(seatsRemaining + 1);
      }
      await queryClient.invalidateQueries({ queryKey: ['dropdown-table'] });
      dispatch(openSnackbar({ severity: 'warning', message: `user ${user.username} disabled!` }));
    } catch (err) {
      console.log(err);
      setConfirmationOpen(false);
      setConfirmationReset(false);
      dispatch(openSnackbar({ severity: 'error', message: `user ${selectedUser.username} couldn't be disabled!` }));
    }
  };

  const onEnable = async () => {
    try {
      const user = await enableUserMutation.mutateAsync(selectedUser._id);
      setConfirmationOpen(false);
      replaceStaleUser(user);
      setSelectedUser(user);
      // AWS cognito calls are slow + expensive, so we just update the # of seats
      // the user sees on the client side.
      if (seatsRemaining !== false && user.privilege !== userPrivilegeMap.broadsightAdmin) {
        setSeatsRemaining(seatsRemaining - 1);
      }
      await queryClient.invalidateQueries({ queryKey: ['dropdown-table'] });
      dispatch(openSnackbar({ severity: 'warning', message: `user ${user.username} enabled!` }));
    } catch (err) {
      console.log(err);
      setConfirmationOpen(false);
      dispatch(openSnackbar({ severity: 'error', message: `user ${selectedUser.username} couldn't be enabled!` }));
    }
  };

  const onResetMFA = async () => {
    try {
      await resetUserMfaMutation.mutateAsync(selectedUser.id);
      selectedUser.phone = '';
      setConfirmationResetMFA(false);
      replaceStaleUser(selectedUser);
      dispatch(openSnackbar({ severity: 'warning', message: `user ${selectedUser.username}'s MFA settings reset!` }));
    } catch (err) {
      console.log(err);
      setConfirmationOpen(false);
      setConfirmationReset(false);
      dispatch(
        openSnackbar({
          severity: 'error',
          message: `user ${selectedUser.username}'s MFA settings couldn't be reset!`,
        })
      );
    }
  };

  const disableAddUserButton =
    tenantSeatsQuery.isLoading || tenantSeatsQuery.isError || (seatsRemaining !== false && seatsRemaining <= 0);
  const AddUserButton = () => {
    return (
      <Button
        sx={{ mt: '2vh', mb: '2vh' }}
        color="primary"
        variant="contained"
        onClick={() => setAddUserOpen(true)}
        disabled={disableAddUserButton}
      >
        <AddIcon />
        Add User
      </Button>
    );
  };

  const AddEnumButton = () => {
    return (
      <Button
        sx={{ mt: '2vh', mb: '2vh' }}
        color="primary"
        variant="contained"
        onClick={() => {
          setEnumModalOpen(true);
          setIsEditing(false);
        }}
      >
        <AddIcon />
        Add {category.slice(0, -1)}
      </Button>
    );
  };

  // const executeFunction = async (parameter) => {
  //   try {
  //     const response = await fetch('/api/executeFunction', {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //       },
  //       body: JSON.stringify({ parameter }),
  //     });

  //     const data = await response.json();
  //     console.log(data.result); // Result returned by the backend function
  //   } catch (error) {
  //     console.error('Error executing function:', error);
  //   }
  // };

  // // Example usage of executeFunction with a parameter
  // const handleExecute = async (parameter) => {
  //   const parameterValue = parameter;

  //   try {
  //     await executeFunction(parameterValue);
  //   } catch (error) {
  //     console.error('Error executing function:', error);
  //   }
  // };

  // This is a temporary fix when upgrading to react-query v5 because onSuccess() is deprecated in useQuery.
  // Check out why it's strongly NOT encouraged anymore: https://tkdodo.eu/blog/breaking-react-querys-api-on-purpose#state-syncing
  useEffect(() => {
    if (usersQuery.data) setUsers(usersQuery.data.data);
    if (tenantSeatsQuery.data) setSeatsRemaining(tenantSeatsQuery.data.noOfTenantSeatsRemaining);
  }, [usersQuery.data, tenantSeatsQuery.data]);

  const activeCategoryQueries = categoryQueryStates[category] ?? [];
  const isCategoryLoading = activeCategoryQueries.some((query) => query.isLoading);
  const isCategoryError = activeCategoryQueries.some((query) => query.isError);

  if (isCategoryLoading) return <Box sx={spinnerSx}>Loading...</Box>;
  if (isCategoryError) {
    return (
      <Box sx={spinnerSx}>
        <Typography variant="h3">Error loading users or units</Typography>
        <Typography variant="h5">Please refresh the page</Typography>
      </Box>
    );
  }

  return (
    <Stack
      direction="column"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignContent: 'space-between',
        width: '80%',
        minHeight: '85vh',
        ml: '5%',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', ml: '5rem', mb: 0 }}>
        <AdminCategorySelect category={category} handleCategoryChange={handleCategoryChange} />
      </Box>
      {category === 'Users' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
            height: '100%',
            p: '1rem',
            ml: '5rem',
            mt: '1rem',
            background: (theme) => theme.palette.whiteScaleDefault.main,
            borderRadius: '1rem',
          }}
        >
          <Stack
            sx={{
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              mt: '0.5vh',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h5">Users in your organization</Typography>
              {!tenantSeatsQuery.isLoading && !tenantSeatsQuery.isError && seatsRemaining !== false && (
                <Stack justifyContent="center" alignItems="center" direction="row" gap={1}>
                  <Typography>Seats remaining: {seatsRemaining}</Typography>
                  <Tooltip
                    title={<Typography variant={'body2'}>The calculation ignores users who are deactivated</Typography>}
                    TransitionComponent={Zoom}
                    enterDelay={200}
                    placement={'top'}
                  >
                    <div>
                      <HelpOutlineIcon />
                    </div>
                  </Tooltip>
                </Stack>
              )}
              <UserListComponent
                clickCallBack={setSelectedUser}
                user={selectedUser}
                users={users}
                permissionEditable={true}
                addUserButton={AddUserButton}
                getAvatarLetter={getAvatarLetter}
                getListItemTitle={getListItemTitle}
                getListItemSubTitle={getListItemSubTitle}
                editingUserPrivilege={editingUserPrivilege}
                requiredEditingUserPrivilege={PrivilegeLevels.admin.level}
              ></UserListComponent>
            </div>
            {selectedUser && (
              <Box sx={{ ml: '3vw', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {!updateUserMutation.isPending && (
                  <Box
                    sx={{
                      border: (theme) => `1px solid ${theme.palette.whiteScale.main}`,
                      borderRadius: '10px',
                      backgroundColor: (theme) => theme.palette.whiteScaleLighter.main,
                      p: '0.75rem',
                      maxWidth: '560px',
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 700, fontSize: '1.25rem', textAlign: 'center', mb: '0.65rem' }}
                    >
                      Admin Controls
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'center' }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => setConfirmationOpen(true)}
                        sx={
                          selectedUser.enabled === 'True'
                            ? {
                                backgroundColor: (theme) => theme.palette.toneNegative.main,
                                minHeight: '34px',
                                minWidth: '150px',
                                color: (theme) => theme.palette.whiteScaleLighter.main,
                              }
                            : {
                                backgroundColor: (theme) => theme.palette.tertiaryDarker.main,
                                minHeight: '34px',
                                minWidth: '150px',
                                color: (theme) => theme.palette.tertiaryDarker.contrastText,
                              }
                        }
                        disabled={selectedUser.enabled === 'False' && disableAddUserButton}
                      >
                        {selectedUser.enabled === 'True' ? 'Disable User' : 'Enable User'}
                      </Button>
                      {hasAdminPrivilege() && (
                        <Button
                          variant="contained"
                          size="small"
                          data-cy="button-resetUserMFA-AdminPage"
                          onClick={() => setConfirmationResetMFA(true)}
                          sx={
                            selectedUser.mfaEnabled === 'Enabled'
                              ? {
                                  backgroundColor: (theme) => theme.palette.toneNegative.main,
                                  minHeight: '34px',
                                  minWidth: '150px',
                                  color: (theme) => theme.palette.whiteScaleLighter.main,
                                }
                              : {
                                  backgroundColor: (theme) => theme.palette.tertiaryDarker.main,
                                  minHeight: '34px',
                                  minWidth: '150px',
                                  color: (theme) => theme.palette.tertiaryDarker.contrastText,
                                }
                          }
                          disabled={selectedUser.mfaEnabled === 'Disabled'}
                        >
                          {selectedUser.mfaEnabled === 'Enabled' ? 'Reset SMS MFA' : 'MFA Not Configured'}
                        </Button>
                      )}
                      {hasAdminPrivilege() && selectedUser.status === 1 && (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => setConfirmationReset(true)}
                          sx={{
                            backgroundColor: (theme) => theme.palette.primaryLight.main,
                            minHeight: '34px',
                            minWidth: '180px',
                            color: (theme) => theme.palette.primaryLight.contrastText,
                          }}
                        >
                          Resend Invitation Email
                        </Button>
                      )}
                    </Box>
                  </Box>
                )}
                <ProfileComponent
                  user={selectedUser}
                  setUser={(user) => {
                    if (selectedUser === user) {
                      dispatch(openSnackbar({ severity: 'error', message: `User wasn't updated!` }));
                    } else {
                      dispatch(
                        openSnackbar({
                          severity: 'warning',
                          message: `User updated! User will have to click reset password before first logging in.`,
                        })
                      );
                    }
                    setSelectedUser(user);
                    updateUserList(user);
                  }}
                  editingUserPrivilege={editingUserPrivilege}
                  updateUserMutation={updateUserMutation}
                ></ProfileComponent>
              </Box>
            )}
          </Stack>
          {confirmationOpen && selectedUser && (
            <ConfirmationModal
              open={confirmationOpen}
              setOpen={setConfirmationOpen}
              headerText={(selectedUser.enabled === 'True' ? 'Disable ' : 'Enable ') + selectedUser.username}
              text={`Are you sure you want to ${selectedUser.enabled === 'True' ? 'disable' : 'enable'} this user?`}
              callBack={selectedUser.enabled === 'True' ? onDisable : onEnable}
              confirmButtonText={selectedUser.enabled === 'True' ? 'Disable' : 'Enable'}
              destructive={selectedUser.enabled === 'True'}
            ></ConfirmationModal>
          )}
          {confirmationResetMFA && selectedUser && (
            <ConfirmationModal
              open={confirmationResetMFA}
              setOpen={setConfirmationResetMFA}
              headerText={'Reset SMS MFA settings for ' + selectedUser.username}
              text={`Are you sure you want to reset this user's SMS MFA configuration?`}
              callBack={onResetMFA}
              confirmButtonText={'Reset SMS MFA'}
              destructive
            ></ConfirmationModal>
          )}
          {confirmationReset && selectedUser && (
            <ConfirmationModal
              open={confirmationReset}
              editingUserPrivilege={editingUserPrivilege}
              setOpen={setConfirmationReset}
              headerText={'Resend invitation email'}
              text={
                'Please confirm you want to resend the Broadsight Tracker invitation email for this user. If they do not receive the email, please double-check their email address is correct, and ask them to check their spam or trash folders.'
              }
              callBack={onReset}
              confirmButtonText={selectedUser.enabled === 'True' ? 'Resend Invitation' : 'Cancel'}
            ></ConfirmationModal>
          )}
          {addUserOpen && (
            <AddUserModal
              users={users}
              open={addUserOpen}
              editingUserPrivilege={editingUserPrivilege}
              setOpen={setAddUserOpen}
              callback={addUserToList}
            ></AddUserModal>
          )}
        </Box>
      )}

      {category === 'Units' && (
        <EnumListContainer
          title="Unit List"
          itemType="Unit"
          enumItems={unitsQuery.data}
          addButton={AddEnumButton}
          enumModalOpen={enumModalOpen}
          setEnumModalOpen={setEnumModalOpen}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          isMerging={isMerging}
          setIsMerging={setIsMerging}
          selectedEnum={selectedEnum}
          setSelectedEnum={setSelectedEnum}
        />
      )}

      {category === 'Types' && (
        <EnumListContainer
          title="Types List"
          itemType="Type"
          enumItems={typesQuery.data}
          addButton={AddEnumButton}
          enumModalOpen={enumModalOpen}
          setEnumModalOpen={setEnumModalOpen}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          isMerging={isMerging}
          setIsMerging={setIsMerging}
          selectedEnum={selectedEnum}
          setSelectedEnum={setSelectedEnum}
        />
      )}

      {category === 'Departments' && (
        <EnumListContainer
          title="Departments List"
          itemType="Department"
          enumItems={deptsQuery.data.data}
          addButton={AddEnumButton}
          enumModalOpen={enumModalOpen}
          setEnumModalOpen={setEnumModalOpen}
          isEditing={isEditing}
          isMerging={isMerging}
          setIsMerging={setIsMerging}
          setIsEditing={setIsEditing}
          selectedEnum={selectedEnum}
          setSelectedEnum={setSelectedEnum}
        />
      )}

      {category === 'Campaigns' && (
        <EnumListContainer
          title="Campaign List"
          itemType="Campaign"
          enumItems={campaignsQuery.data}
          addButton={AddEnumButton}
          enumModalOpen={enumModalOpen}
          setEnumModalOpen={setEnumModalOpen}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          isMerging={isMerging}
          setIsMerging={setIsMerging}
          selectedEnum={selectedEnum}
          setSelectedEnum={setSelectedEnum}
        />
      )}

      {category === 'Audiences' && (
        <EnumListContainer
          title="Audience List"
          itemType="Audience"
          enumItems={audiencesQuery.data}
          addButton={AddEnumButton}
          enumModalOpen={enumModalOpen}
          setEnumModalOpen={setEnumModalOpen}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          isMerging={isMerging}
          setIsMerging={setIsMerging}
          selectedEnum={selectedEnum}
          setSelectedEnum={setSelectedEnum}
        />
      )}

      {category === 'Custom Fields' && (
        <CustomFieldsContainer
          expertCustomFields={expertCustomFieldsQuery.data}
          journalistCustomFields={journalistCustomFieldsQuery.data}
          outletCustomFields={outletCustomFieldsQuery.data}
          communityMemberCustomFields={communityMemberCustomFieldsQuery.data}
          organizationCustomFields={organizationCustomFieldsQuery.data}
          audienceCustomFields={[]}
          showEngagementCustomFields={engagementLogActivated}
          showAudienceCustomFields={false}
        />
      )}
    </Stack>
  );
}

export default AdminPage;
